/**
 * BulletEditor — nested bullets with a yellow/green/red tone per line.
 * Shared by the stakeholder update letter sections and the schedule rows.
 */
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, IndentIncrease, IndentDecrease } from "lucide-react";

const TONES = [
  { key: "none", cls: "bg-white border-slate-300", label: "No highlight" },
  { key: "yellow", cls: "bg-yellow-200 border-yellow-400", label: "Pending" },
  { key: "green", cls: "bg-green-200 border-green-400", label: "Complete" },
  { key: "red", cls: "bg-red-200 border-red-400", label: "Blocked" },
];

export const TONE_BG = {
  none: "", yellow: "bg-yellow-200", green: "bg-green-200", red: "bg-red-200",
};

export const BulletEditor = ({ bullets = [], onChange, testid }) => {
  const set = (i, patch) => onChange(bullets.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className="space-y-1" data-testid={testid}>
      {bullets.map((b, i) => (
        <div key={i} className="flex items-center gap-1" style={{ paddingLeft: `${(b.level || 0) * 18}px` }}
          data-testid={`${testid}-row-${i}`}>
          <Input className={`h-8 text-xs ${TONE_BG[b.tone] || ""}`} value={b.text || ""}
            placeholder="Bullet text"
            onChange={(e) => set(i, { text: e.target.value })} />
          <div className="flex items-center gap-0.5">
            {TONES.map((t) => (
              <button key={t.key} type="button" title={t.label}
                className={`w-4 h-4 rounded-sm border ${t.cls} ${(b.tone || "none") === t.key ? "ring-2 ring-slate-900" : ""}`}
                onClick={() => set(i, { tone: t.key })}
                data-testid={`${testid}-tone-${i}-${t.key}`} />
            ))}
          </div>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
            onClick={() => set(i, { level: Math.max(0, (b.level || 0) - 1) })}>
            <IndentDecrease className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
            onClick={() => set(i, { level: Math.min(2, (b.level || 0) + 1) })}>
            <IndentIncrease className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
            onClick={() => onChange(bullets.filter((_, j) => j !== i))}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-7 text-xs"
        onClick={() => onChange([...bullets, { text: "", level: 0, tone: "none" }])}
        data-testid={`${testid}-add`}>
        <Plus className="w-3 h-3 mr-1" /> Bullet
      </Button>
    </div>
  );
};

export const SectionsEditor = ({ sections = [], onChange, testid, placeholder = "Section heading" }) => (
  <div className="space-y-3" data-testid={testid}>
    {sections.map((s, i) => (
      <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`${testid}-section-${i}`}>
        <div className="flex items-center gap-2 mb-1.5">
          <Input className="h-8 text-xs font-semibold" value={s.heading || ""} placeholder={placeholder}
            onChange={(e) => onChange(sections.map((x, j) => (j === i ? { ...x, heading: e.target.value } : x)))} />
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
            onClick={() => onChange(sections.filter((_, j) => j !== i))}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
        <BulletEditor bullets={s.bullets || []} testid={`${testid}-${i}-bullets`}
          onChange={(b) => onChange(sections.map((x, j) => (j === i ? { ...x, bullets: b } : x)))} />
      </div>
    ))}
    <Button size="sm" variant="outline" className="h-8 text-xs"
      onClick={() => onChange([...sections, { heading: "", bullets: [] }])}
      data-testid={`${testid}-add-section`}>
      <Plus className="w-3.5 h-3.5 mr-1" /> Section
    </Button>
  </div>
);
